import { FC } from "react";
import { useTranslation } from "react-i18next";

import { ButtonLink } from "@/components/Interface/Button/Button";
import { usePortalDeviceTiles } from "@/components/Portal/components/PortalDeviceTiles/hooks/usePortalDeviceTiles.ts";
import { TilesGrid } from "@/components/Portal/components/PortalDeviceTiles/TilesGrid/TilesGrid";
import { PortalSection } from "@/components/Portal/components/PortalSection/PortalSection";
import { useNeedsIDCheck } from "@/hooks/useNeedsIDCheck.ts";
import { portalRoutes } from "@/router/routes/portalRoutes.ts";

export const COCKPIT_ABOS_MAX_TILES = 5;

interface CockpitAbosSectionProps {
  manageRoute: string;
}

/**
 * Shows a preview of the customer's active subscriptions on the Cockpit dashboard.
 * Loads up to the priority-sort fetch cap (100), sorts by display priority, then
 * shows the first five tiles and a link when more plans exist.
 */
export const CockpitAbosSection: FC<CockpitAbosSectionProps> = ({
  manageRoute,
}) => {
  const { t } = useTranslation("portal");
  const needsIdCheck = useNeedsIDCheck();
  const {
    flatSimList,
    flatExtendedSimList,
    isLoading,
    isFetchingNextPage,
    fetchNextPage,
    isError,
    hasNextPage,
    shouldShowItemizedBillPromoCard,
    pageSize,
    totalElements,
  } = usePortalDeviceTiles(false, true, {
    sortByDisplayPriority: true,
  });

  if (!isLoading && flatSimList.length === 0) {
    return null;
  }

  const totalCount = totalElements ?? flatSimList.length;
  const remainingCount = totalCount - COCKPIT_ABOS_MAX_TILES;
  const showMoreButton = !isLoading && remainingCount > 0;

  return (
    <PortalSection
      titleKey="cockpit.dashboard.quickLinks.title"
      action={
        <ButtonLink
          data-testid="cockpit-abos-show-all"
          to={portalRoutes.mySubscriptions.base}
          variant="secondary"
        >
          {t("cockpit.dashboard.quickLinks.showAll")}
        </ButtonLink>
      }
    >
      <div className="flex flex-col gap-4">
        <TilesGrid
          flatSimList={flatSimList}
          flatExtendedSimList={flatExtendedSimList}
          isLoading={isLoading}
          isFetchingNextPage={isFetchingNextPage}
          hasNextPage={!!hasNextPage}
          fetchNextPage={fetchNextPage}
          isError={isError}
          shouldShowItemizedBillPromoCard={shouldShowItemizedBillPromoCard}
          showMetaTiles={false}
          showSims={true}
          needsIdCheck={needsIdCheck}
          manageRoute={manageRoute}
          pageSize={pageSize}
          useInfiniteScroll={false}
          fetchAllPages={false}
          maxTiles={COCKPIT_ABOS_MAX_TILES}
          autoHeight={true}
        />
        {/* Full width on mobile, centred and content-sized from tablet up.
            `ButtonLink` wraps its anchor in a div, so the alignment has to come
            from this parent - `self-*` on the component itself is inert. */}
        {showMoreButton && (
          <div className="flex flex-col tablet:items-center">
            <ButtonLink
              data-testid="cockpit-abos-show-more"
              className="w-full tablet:w-auto"
              to={portalRoutes.mySubscriptions.base}
            >
              {t("cockpit.dashboard.quickLinks.showMoreAbos", {
                count: remainingCount,
              })}
            </ButtonLink>
          </div>
        )}
      </div>
    </PortalSection>
  );
};
